import { z } from "zod";

/** The lowest limit or stock the seller states, however high; null where
 *  it states none and tracks no stock: no limit. Lissen adds none of its
 *  own. */
export function sellerQuantityLimit(...limits: (number | null | undefined)[]): number | null {
  const known = limits.filter((limit): limit is number => limit !== null && limit !== undefined);
  return known.length ? Math.min(...known) : null;
}

/** A limit as an order counts it: never fewer than one; none stays none. */
export const atLeastOne = (limit: number | null): number | null =>
  limit === null ? null : Math.max(1, limit);

/** The most of a ticket type one order may hold, as the quote and the
 *  purchase sheets read it: its seller's limit or stock, or no bound at all
 *  (Infinity) where the seller states none (`maxQuantity` absent or null). */
export const sellerLineMost = (ticket: { maxQuantity?: number | null }): number =>
  ticket.maxQuantity ?? Infinity;

/** A ticket type of a vendor's event. `ticketId` is the vendor's own id, in
 * the form its rules (`vendorCatalogRules`) give; the journey checks it. */
export const vendorTicketTypeSchema = z
  .object({
    id: z.string().min(1).max(100),
    ticketId: z.string().min(1),
    name: z.string().trim().min(1).max(240),
    priceMinor: z.number().int().nonnegative(),
    available: z.boolean().optional(),
    bookingFeeMinor: z.number().int().nonnegative(),
    minQuantity: z.number().int().min(1),
    /** The most of it one order may hold: the seller's own limit (one it
     *  states, or its stock where it tracks it), however high. Absent where
     *  the seller states none and tracks no stock: no limit
     *  (`sellerLineMost`). */
    maxQuantity: z.number().int().min(1).optional(),
    /** A merch product's values of its options, one per the journey's
     *  `optionNames`, in their order (["M", "Canvas"]). Absent for tickets,
     *  and for a product whose store gives none. Not strict, so a build
     *  without it drops it; a reader that finds them not matching the
     *  journey's names uses neither (`optionSetOf`). */
    options: z.array(z.string().trim().min(1).max(240)).min(1).max(4).optional(),
  })
  .refine((ticket) => ticket.maxQuantity === undefined || ticket.maxQuantity >= ticket.minQuantity);

/** One step of a vendor's delivery-fee ladder, in minor units. `maxMinor` is
 * the inclusive upper bound of the tickets subtotal (booking fees included);
 * the last tier has none. */
export const vendorDeliveryFeeTierSchema = z
  .object({
    maxMinor: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .describe("The most the tickets subtotal (booking fees included) can be for this tier's fee to apply, in minor units (pence, cents); null on the last tier, which has no upper bound."),
    feeMinor: z
      .number()
      .int()
      .nonnegative()
      .describe("The delivery fee this tier charges, in minor units (pence, cents)."),
  })
  .strict()
  .describe("One step of the delivery-fee ladder.");
export type VendorDeliveryFeeTier = z.infer<typeof vendorDeliveryFeeTierSchema>;

/** Ascending `maxMinor`, and only the last tier may be open-ended. */
export const vendorDeliveryFeeTiersSchema = z
  .array(vendorDeliveryFeeTierSchema)
  .max(20)
  .refine((tiers) =>
    tiers.every((tier, index) => {
      if (tier.maxMinor === null) return index === tiers.length - 1;
      const previous = tiers[index - 1]?.maxMinor;
      return previous === undefined || (previous !== null && tier.maxMinor > previous);
    }),
  )
  .describe("A seller's delivery-fee ladder: tiers in ascending `maxMinor`, only the last open-ended (null `maxMinor`). To save a listing, pass the `deliveryFeeTiers` its load returned.");

/** An ISO 4217 currency code ("GBP", "USD"). */
export const storeCurrencySchema = z.string().regex(/^[A-Z]{3}$/);

/** The estimate in the fan's own currency beside a journey's prices (the
 *  listing design's §6): `rate` units of `currency`, the fan country's, for
 *  one unit of the journey's, as decimal text ("0.84612000"), crossed
 *  through the dollar from Open Exchange Rates' rates for `date`. Clients
 *  show "about £64" from it (`formatApprox`). */
export const journeyApproxSchema = z.object({
  currency: storeCurrencySchema,
  rate: z.string().regex(/^\d+(?:\.\d+)?$/),
  date: z.iso.date(),
});
export type JourneyApprox = z.infer<typeof journeyApproxSchema>;

/** Why a listing is a demo listing, as the server's seller markets decide
 *  it (`@libs/buy-on-behalf` `seller-markets.ts`): `seller`, 1-tap buys
 *  nothing from its vendor (a demo-only vendor, or one whose purchases are
 *  off); `currency`, the vendor buys, but not in the listing's currency;
 *  `country`, merch only: the vendor buys in that currency, but not for
 *  delivery to the fan's country. */
export const demoReasons = ["seller", "currency", "country"] as const;
export type DemoReason = (typeof demoReasons)[number];


/** A listing's prices as its headline reads them: its currency, its flat
 *  order fee and its ticket types (a merch product's sizes). The server's
 *  journey (\`@libs/buy-on-behalf\` \`seller-data\`) is one. */
export type ListingPricing = {
  currency: string;
  orderFeeMinor: number;
  tickets: readonly z.infer<typeof vendorTicketTypeSchema>[];
};

/** The quantities a buyer chose, by ticket type. A line's quantity is
 *  bounded by its ticket type's own limits, which the quote checks
 *  (`quoteVendorSelection`), never here. */
/** The most tickets or variants one listing holds: an eBilet seat map's
 *  sections and the tickets in each run past a hundred (a large arena's). */
export const MOST_LISTED_TICKETS = 500;

export const vendorSelectionSchema = z
  .array(
    z
      .object({
        item_id: z.string().min(1).max(100),
        quantity: z.number().int().min(1),
      })
      .strict(),
  )
  .min(1)
  .max(MOST_LISTED_TICKETS)
  .refine(
    (lines) => new Set(lines.map((line) => line.item_id)).size === lines.length,
  );
export type VendorSelection = z.infer<typeof vendorSelectionSchema>;

/** What a buyer pays per ticket line before any order-level fee. */
export function vendorTicketsSubtotalMinor(
  lines: Iterable<{ quantity: number; priceMinor: number; bookingFeeMinor: number }>,
): number {
  let total = 0;
  for (const line of lines)
    total += line.quantity * (line.priceMinor + line.bookingFeeMinor);
  return total;
}

/** The delivery fee for a tickets subtotal: the first tier whose `maxMinor`
 * is open or at least the subtotal. No tiers, no fee. */
export function vendorDeliveryFeeMinor(
  tiers: readonly VendorDeliveryFeeTier[] | undefined,
  ticketSubtotalMinor: number,
): number {
  if (ticketSubtotalMinor <= 0) return 0;
  const tier = tiers?.find(
    (t) => t.maxMinor === null || t.maxMinor >= ticketSubtotalMinor,
  );
  return tier?.feeMinor ?? 0;
}

/**
 * The ladder corrected by a fee the vendor charged: its fee for exactly that
 * subtotal is the one observed, every other subtotal's as before (a vendor
 * basket's postage that differed from the stored ladder, so the next quote
 * of that order is right). Neighbouring steps of one fee are merged; past a
 * closed last step, or with no ladder, the fee is none. Null when the
 * correction would hold more steps than a journey takes.
 */
export function vendorDeliveryFeeTiersWith(
  tiers: readonly VendorDeliveryFeeTier[],
  subtotalMinor: number,
  feeMinor: number,
): VendorDeliveryFeeTier[] | null {
  if (subtotalMinor <= 0 || vendorDeliveryFeeMinor(tiers, subtotalMinor) === feeMinor)
    return [...tiers];
  const open = tiers.at(-1)?.maxMinor === null;
  const steps: VendorDeliveryFeeTier[] = [];
  let previous = 0;
  for (const tier of open ? tiers : [...tiers, { maxMinor: null, feeMinor: 0 }]) {
    const covers =
      subtotalMinor > previous &&
      (tier.maxMinor === null || subtotalMinor <= tier.maxMinor);
    if (!covers) steps.push(tier);
    else {
      if (subtotalMinor - 1 > previous)
        steps.push({ maxMinor: subtotalMinor - 1, feeMinor: tier.feeMinor });
      steps.push({ maxMinor: subtotalMinor, feeMinor });
      if (tier.maxMinor === null || tier.maxMinor > subtotalMinor)
        steps.push({ maxMinor: tier.maxMinor, feeMinor: tier.feeMinor });
    }
    previous = tier.maxMinor ?? Infinity;
  }
  const merged = steps.filter(
    (step, index) => steps[index + 1]?.feeMinor !== step.feeMinor,
  );
  if (merged.length === 1 && merged[0]!.feeMinor === 0) return [];
  return merged.length > 20 ? null : merged;
}

/** The single per-order fee of a vendor order: the journey's flat fee plus the
 * delivery fee its tiers give for the subtotal (booking fees included). Every
 * client, the start validation and the worker's pre-Pay check use this. An
 * empty selection costs nothing. */
export function vendorOrderFeeMinor(
  journey: Pick<ListingPricing, "orderFeeMinor"> & {
    deliveryFeeTiers?: readonly VendorDeliveryFeeTier[];
  },
  ticketSubtotalMinor: number,
): number {
  if (ticketSubtotalMinor <= 0) return 0;
  return (
    journey.orderFeeMinor +
    vendorDeliveryFeeMinor(journey.deliveryFeeTiers, ticketSubtotalMinor)
  );
}
